import { buildApiUrl, getAuthHeaders, API_CONFIG } from "@/config/api";
import type { User } from "@/types/auth";

export type UserRole = User["role"]; // "ADMIN" | "ANALYST" | "REVIEWER" | "VIEWER"

export interface UserSearchParams {
  q?: string;
  role?: UserRole;
  page?: number;     // 1-based
  pageSize?: number; // preferred name
  limit?: number;    // accepted alias (mapped to pageSize)
  ids?: string[];    // optional: fetch specific ids
}

export interface UserSearchResult {
  users: User[];
  items: User[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  nextPage: number | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
  };
}

class UserService {
  async listAll(): Promise<User[]> {
    const url = buildApiUrl(`${API_CONFIG.ENDPOINTS.AUTH}/users`);
    const res = await fetch(url, { method: "GET", headers: getAuthHeaders() });
    if (!res.ok) throw new Error(`Fetch users failed: ${res.status} ${await res.text()}`);
    const json = await res.json();
    return json.users ?? json.data ?? [];
  }
  async search(params: UserSearchParams = {}): Promise<UserSearchResult> {
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? params.limit ?? 20;
    const q = params.q ?? "";
    const role = params.role;
    const ids = params.ids;

    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (role) qs.set("role", role);
    if (ids?.length) qs.set("ids", ids.join(","));
   qs.set("page", String(page));
qs.set("limit", String(pageSize)); 

const candidates = [
  buildApiUrl(`${API_CONFIG.ENDPOINTS.AUTH}/users?${qs.toString()}`),
];


    for (const url of candidates) {
      try {
        const res = await fetch(url, { method: "GET", headers: getAuthHeaders(), credentials: "include" });
        if (res.status === 404) continue; // try next candidate
        if (!res.ok) {
          const body = await res.text().catch(() => "");
          throw new Error(`User search failed: ${res.status} ${body}`);
        }

        const json = await res.json().catch(() => ({} as any));
        const users: User[] = json.users ?? json.items ?? json.data ?? json.results ?? [];

        const total: number =
          json.total ?? json.count ?? json.pagination?.total ?? users.length ?? 0;
        const serverPage: number = json.page ?? json.pagination?.page ?? page;
        const serverPageSize: number = json.pageSize ?? json.limit ?? json.pagination?.limit ?? pageSize;

        const totalPages =
          json.totalPages ??
          json.pagination?.totalPages ??
          (serverPageSize > 0 ? Math.max(1, Math.ceil(total / serverPageSize)) : 1);

        const hasMore: boolean =
          json.hasMore ??
          json.pagination?.hasNext ??
          (total > serverPage * serverPageSize);

        const nextPage = hasMore ? serverPage + 1 : null;

        return {
          users,
          items: users,
          total,
          page: serverPage,
          pageSize: serverPageSize,
          hasMore,
          nextPage,
          pagination: {
            page: serverPage,
            limit: serverPageSize,
            total,
            totalPages,
            hasNext: !!hasMore,
          },
        };
      } catch {
      }
    }

    const all = await this.listAll();
    const filtered = all.filter((u) => {
      const byRole = role ? u.role === role : true;
      const byQuery = q
        ? `${u.firstName ?? ""} ${u.lastName ?? ""} ${u.email ?? ""}`
            .toLowerCase()
            .includes(q.toLowerCase())
        : true;
      const byIds = ids?.length ? ids.includes(u.id) : true;
      return byRole && byQuery && byIds;
    });

    const start = (page - 1) * pageSize;
    const slice = filtered.slice(start, start + pageSize);
    const total = filtered.length;
    const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1;
    const hasMore = start + slice.length < total;

    return {
      users: slice,
      items: slice,
      total,
      page,
      pageSize,
      hasMore,
      nextPage: hasMore ? page + 1 : null,
      pagination: {
        page,
        limit: pageSize,
        total,
        totalPages,
        hasNext: hasMore,
      },
    };
  }
  async getByIds(ids: string[]): Promise<User[]> {
    if (!ids?.length) return [];

    const qs = new URLSearchParams({ ids: ids.join(",") }).toString();
    const candidates = [
      buildApiUrl(`${API_CONFIG.ENDPOINTS.AUTH}/users/by-ids?${qs}`),
      buildApiUrl(`${API_CONFIG.ENDPOINTS.AUTH}/users?${qs}`),
    ];

    for (const url of candidates) {
      try {
        const res = await fetch(url, { method: "GET", headers: getAuthHeaders() });
        if (res.status === 404) continue;
        if (!res.ok) throw new Error(`Fetch users by IDs failed: ${res.status} ${await res.text()}`);
        const json = await res.json();
        const users: User[] = json.users ?? json.data ?? json.results ?? [];
        if (users.length) return users;
      } catch {
      }
    }

    const all = await this.listAll();
    return all.filter((u) => ids.includes(u.id));
  }
}

export const userService = new UserService();
export default userService;
